
/**
 * HistoricalFondsPrices for lbbw-am.de
 *
 * @author Florian Gutwald
 * Converted to Plain JS for Rollup
 */
import Handlebars from 'handlebars';
import _ from 'lodash';
import moment from 'moment';
//import Tablesaw from 'tablesaw';
import AMApp from '../../global/app';
import Pagination from '../../gui/Pagination';

export default class HistoricalFondsPrices {
  /**
   * Constructor
   * @param {HTMLElement} element
   * @param {Object} options
   */
  constructor(element, options = {}) {
    this.element = element;
    this.amApp = new AMApp();

    this.settings = Object.assign({
      routeAllFonds: '?eID=fonds&function=allFonds',
      routeHistory: '?eID=fonds&function=history&'
    }, options);

    this.headers = {
      INSD_ISIN: 'ISIN',
      INHI_DATUM: 'Datum',
      INHI_AUSGABEPREIS: 'Ausgabepreis',
      INHI_RUECKNAHMEPREIS: 'Rücknahmepreis',
      INHI_KAPITALBETEILIGUNG_ID935: 'Kapitalbeteiligung',
      INHI_IMMOBILIENQUOTE_ID937: 'Immobilienquote',
      INHI_ZWISCHENGEWINN: 'Zwischengewinn',
      INHI_AKTIENGEWINN_PAR8B: 'AktienG. § 8b KStG',
      INHI_AKTIENGEWINN: 'AktienG. § 3 Nr 40 EStG AktienG. § 8b',
      INHI_IMMOBILIENGEWINN: 'Immobiliengewinn'
    };

    this.showResultAmount = 25;
    this.dataStore = null;
    this.selectedFond = null;

    // DOM Elements
    this.resultTemplate = null;
    this.resultTable = null;
    this.resultTarget = null;
    this.resultContainer = null;
    this.statusInit = null;
    this.statusNoResult = null;
    this.form = null;
    this.searchButton = null;
    this.resetButton = null;

    this.paginationInstances = {};

    this.init();
  }

  /**
   * Initialize
   */
  init() {
    this.cacheDOM();
    this.registerHandlebarsHelpers();
    this.attachEventListeners();
    this.getAvailableFonds();
    this.rangeEndYear();
  }

  /**
   * Cache DOM elements
   */
  cacheDOM() {
    const templateEl = document.getElementById('historicalfondsprices_template');
    this.resultTemplate = templateEl ? templateEl.innerHTML : '';
    this.resultTable = document.getElementById('historicalfondsprices_table');
    this.resultTarget = document.getElementById('historicalfondsprices_result');
    this.resultContainer = document.getElementById('fonds_result_container');
    this.statusInit = document.querySelector('.js_status_init');
    this.statusNoResult = document.querySelector('.js_status_noresult');
    this.form = document.querySelector('[name=historicalfondsprices]');
    this.searchButton = document.getElementById('fondsprices_search');
    this.resetButton = document.getElementById('fondsprices_reset');
  }

  /**
   * Register Handlebars helpers
   */
  registerHandlebarsHelpers() {
    Handlebars.registerHelper("euroFromFloat", (value) => {
      if (value) {
        return (value.toFixed(2) + ' EUR').replace('.', ',');
      } else if (value === '0' || value === '00' || value === 0) {
        return '0,00 EUR';
      }
      return value;
    });

    Handlebars.registerHelper("sqlTimeToDate", (value) => {
      if (value) {
        return moment(value).format('DD.MM.YYYY');
      }
      return value;
    });

    Handlebars.registerHelper("percentTwoDigits", (value) => {
      if (value || value === 0) {
        return ((value.toFixed(2)) + ' %').replace('.', ',');
      }
      return value;
    });
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    // Form submit
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        const formData = new FormData(this.form);
        const queryString = new URLSearchParams(formData).toString();

        const formObject = {
          type: this.form.getAttribute('method'),
          url: this.form.getAttribute('action'),
          data: queryString
        };

        this.getHistoricalFonds(formObject);
        return false;
      });
    }

    // Download CSV
    document.addEventListener('click', (e) => {
      if (e.target.closest('.js_downloadCSV')) {
        const filename = `HistoricalData ${this.selectedFond}`;
        this.exportCSVFile(filename);
      }
    });

    // Result amount change
    document.addEventListener('change', (e) => {
      if (e.target.id === 'result_amount_top') {
        this.showResultAmount = parseInt(e.target.value, 10);
        this.createHistoryDataAndPager(this.dataStore);
      }
    });

    // Reset button
    document.addEventListener('click', (e) => {
      if (e.target.id === 'fondsprices_reset') {
        setTimeout(() => this.resetFormAndResult(), 250);
      }
    });

    // Fonds selection
    document.addEventListener('click', (e) => {
      const fondItem = e.target.closest('[data-andiid]');
      if (fondItem) {
        e.preventDefault();
        const fondIdInput = document.getElementById('fondId');
        const availableFondsInput = document.getElementById('availablefonds_input');

        if (fondIdInput) fondIdInput.value = fondItem.dataset.andiid;
        if (availableFondsInput) availableFondsInput.value = fondItem.dataset.name;
        if (this.searchButton) this.searchButton.classList.remove('d-none');
      }
    });
  }

  /**
   * Get available fonds via AJAX
   */
  async getAvailableFonds() {
    try {
      const response = await fetch(AMApp.basePath + this.settings.routeAllFonds);
      if (!response.ok) throw new Error('Network response was not ok');

      const result = await response.json();
      if (result) {
        this.appendFondsToDropdown(result);
      }
    } catch (error) {
      console.error('Error fetching available fonds:', error);
    }
  }

  /**
   * Append fonds to dropdown
   */
  appendFondsToDropdown(fonds) {
    const fondIdSelect = document.getElementById('fondId');
    if (!fondIdSelect) return;

    const sortedFonds = _.sortBy(fonds, 'name_textS');
    let dropdownOptions = '';

    sortedFonds.forEach(fond => {
      dropdownOptions += `<option value="${fond.INSD_ANDI_ID}">${fond.name_textS}</option>\n`;
    });

    fondIdSelect.insertAdjacentHTML('beforeend', dropdownOptions);
  }

  /**
   * Set range end year dropdowns
   */
  rangeEndYear() {
    const firstYear = 1998;
    const currentYear = new Date().getFullYear();

    const minYearSelect = document.getElementById('minYear');
    const maxYearSelect = document.getElementById('maxYear');

    if (!minYearSelect || !maxYearSelect) return;

    let dropdownYearsMin = `<option value="${firstYear}">${firstYear}</option>`;
    let dropdownYearsMax = `<option value="${currentYear}">---</option>`;

    for (let year = firstYear; year <= currentYear; year++) {
      dropdownYearsMin += `<option value="${year}">${year}</option>\n`;
      dropdownYearsMax += `<option value="${year}">${year}</option>\n`;
    }

    minYearSelect.insertAdjacentHTML('beforeend', dropdownYearsMin);
    maxYearSelect.insertAdjacentHTML('beforeend', dropdownYearsMax);
  }

  /**
   * Get historical fonds data
   */
  async getHistoricalFonds(formObject) {
    console.log('getHistoricalFonds', formObject);

    const loader = document.createElement('div');
    loader.className = 'loader';
    loader.innerHTML = AMApp.LOADING_ANIMATION;

    if (this.statusInit) {
      this.statusInit.before(loader);
    }

    if (this.resetButton) {
      this.resetButton.classList.remove('d-none');
    }

    const availableFondsInput = document.getElementById('availablefonds_input');
    this.selectedFond = availableFondsInput ? availableFondsInput.value : '';

    this.statusIsLoading();

    try {
      const response = await fetch(AMApp.basePath + this.settings.routeHistory + formObject.data);
      if (!response.ok) throw new Error('Network response was not ok');

      const result = await response.json();

      document.querySelectorAll('.loader').forEach(el => el.remove());

      if (result) {
        const seenDates = new Set();
        const uniqueRows = result.filter(row => {
          if (seenDates.has(row.INHI_DATUM)) return false;
          seenDates.add(row.INHI_DATUM);
          return true;
        });
        this.dataStore = uniqueRows;
        this.createHistoryDataAndPager(uniqueRows);
      }
    } catch (error) {
      console.error('Error fetching historical fonds:', error);
      document.querySelectorAll('.loader').forEach(el => el.remove());
      this.statusError();
    }
  }

  /**
   * Create the native pager
   */
  createHistoryDataAndPager(data) {
    const pagerEl = document.querySelector('.js_result_pager');
    if (!pagerEl) return;

    this.paginationInstances = new Pagination(pagerEl, {
      dataSource: data,
      pageSize: this.showResultAmount,
      callback: (dataFiltered) => {
        this.renderHistoricalData(dataFiltered);
      }
    });
  }

  /**
   * Render historical data
   */
  renderHistoricalData(data) {
    console.log('renderHistoricalData', data);

    if (data.length > 0) {
      this.statusDataFound();

      // Clean null values
      data.forEach(date => {
        Object.entries(date).forEach(([key, value]) => {
          if (value === null) {
            delete date[key];
          }
        });
      });

      const template = Handlebars.compile(this.amApp.toHandlebarsDelimiters(this.resultTemplate));
      if (this.resultTarget) {
        this.resultTarget.innerHTML = template({ data: data });
      }

      if (window.innerWidth < 1280) {
        setTimeout(() => {
          // Tablesaw.init();
        }, 250);
      }
    } else {
      this.statusNoData();
    }
  }

  /**
   * Reset form and result
   */
  resetFormAndResult() {
    if (this.form) {
      const selects = this.form.querySelectorAll('select');
      selects.forEach(select => {
        select.selectedIndex = 0;
      });
    }

    // Keep the container markup: it holds the result table for the next search.
    if (this.resultContainer) {
      this.resultContainer.classList.remove('fondsresult-content__open');
    }

    if (this.resetButton) {
      this.resetButton.classList.add('d-none');
    }

    if (this.statusInit) {
      this.statusInit.classList.remove('d-none');
    }
  }

  /* Status Methods */
  statusIsLoading() {
    if (this.resultTable) {
      this.resultTable.classList.add('fondsresult-table__empty');
    }
    if (this.statusInit) {
      this.statusInit.classList.add('d-none');
    }
    if (this.statusNoResult) {
      this.statusNoResult.classList.add('d-none');
    }

    if (window.innerWidth <= (AMApp.CONSTANTS?.BREAKPOINT?.LG || 1024)) {
      const loader = document.querySelector('.loader');
      if (loader) {
        loader.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  statusNoData() {
    if (this.resultTable) {
      this.resultTable.classList.add('fondsresult-table__empty');
    }
    if (this.resultContainer) {
      this.resultContainer.classList.remove('fondsresult-content__open');
    }
    if (this.statusInit) {
      this.statusInit.classList.add('d-none');
    }
    if (this.statusNoResult) {
      this.statusNoResult.classList.remove('d-none');
    }
  }

  statusDataFound() {
    // Top and bottom action bars; the bottom one holds the pager.
    document.querySelectorAll('.js_result_action').forEach(resultAction => {
      resultAction.classList.add('historicalfondsprices-action__visible');
    });
    if (this.resultContainer) {
      this.resultContainer.classList.add('fondsresult-content__open');
    }
    if (this.resultTable) {
      this.resultTable.classList.remove('fondsresult-table__empty');
    }
    if (this.statusInit) {
      this.statusInit.classList.add('d-none');
    }
    if (this.statusNoResult) {
      this.statusNoResult.classList.add('d-none');
    }
  }

  statusError() {
    if (this.resultTable) {
      this.resultTable.classList.remove('fondsresult-table__empty');
    }
    if (this.resultContainer) {
      this.resultContainer.classList.remove('fondsresult-content__open');
    }
    if (this.statusInit) {
      this.statusInit.classList.add('d-none');
    }
    if (this.statusNoResult) {
      this.statusNoResult.classList.remove('d-none');
    }
  }

  /**
   * Convert JSON to CSV
   */
  convertToCSV(objArray) {
    const dataArray = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
    let str = '';

    for (let i = 0; i < dataArray.length; i += 1) {
      let line = '"';
      for (let index in dataArray[i]) {
        if (line !== '') {
          line += '";"';
        }
        line += dataArray[i][index];
      }
      str += line.substring(3) + '"\r\n';
    }

    return str;
  }

  /**
   * Export CSV file
   */
  exportCSVFile(fileTitle) {
    // Clean data
    const data = this.prepareResult(this.dataStore);

    // Add header
    if (this.headers) {
      data.unshift(this.headers);
    }

    // Convert Object to JSON
    const jsonObject = JSON.stringify(data);
    const csv = this.convertToCSV(jsonObject);

    const exportedFilename = fileTitle + '.csv' || 'export.csv';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

    if (navigator.msSaveBlob) { // IE 10+
      navigator.msSaveBlob(blob, exportedFilename);
    } else {
      const link = document.createElement('a');
      if (link.download !== undefined) { // feature detection
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', exportedFilename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }

  /**
   * Prepare result for export
   */
  prepareResult(data) {
    const cleanedResult = [];

    _.each(data, (line) => {
      const cleanedObject = {
        INSD_ISIN: line.INSD_ISIN,
        INHI_DATUM: this.sqlTimeToDate(line.INHI_DATUM),
        INHI_AUSGABEPREIS: this.floatToEuro(line.INHI_AUSGABEPREIS),
        INHI_RUECKNAHMEPREIS: this.floatToEuro(line.INHI_RUECKNAHMEPREIS),
        INHI_KAPITALBETEILIGUNG_ID935: this.floatToPercentTwoDigits(line.INHI_KAPITALBETEILIGUNG_ID935),
        INHI_IMMOBILIENQUOTE_ID937: this.floatToPercentTwoDigits(line.INHI_IMMOBILIENQUOTE_ID937),
        INHI_ZWISCHENGEWINN: this.floatToEuro(line.INHI_ZWISCHENGEWINN),
        INHI_AKTIENGEWINN_PAR8B: this.floatToPercentTwoDigits(line.INHI_AKTIENGEWINN_PAR8B),
        INHI_AKTIENGEWINN: this.floatToPercentTwoDigits(line.INHI_AKTIENGEWINN),
        INHI_IMMOBILIENGEWINN: this.floatToPercentTwoDigits(line.INHI_IMMOBILIENGEWINN)
      };

      cleanedResult.push(cleanedObject);
    });

    return cleanedResult;
  }

  /* Helper Methods */
  sqlTimeToDate(value) {
    if (value) {
      return moment(value).format('DD.MM.YYYY');
    }
    return value;
  }

  floatToEuro(value) {
    if (value) {
      return (parseFloat(value).toFixed(2) + ' EUR').replace('.', ',');
    }
    return '';
  }

  floatToPercentTwoDigits(value) {
    if (value) {
      return ((parseFloat(value).toFixed(2)) + ' %').replace('.', ',');
    }
    return '';
  }
}
